import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import find from 'lodash/find';

import { ViewerLoader } from '../../Viewer/viewer';
import { IVariantMeshesProperties } from '../../Content/contentModelVariantMesh';
import { IStudioOptions } from '../../Viewer/interface';
import { DecalType } from '~/Scene/Content/Pattern/Decal/patternDecal';

import '../../Configuration/variantMesh.sass';

export class VenumViewer extends ViewerLoader {
    engine: any;

    constructor(project: IStudioOptions, callback: (engine) => void) {
        super({ ...project, configuration: true }, (engine) => {
            this.engine = engine;
            this.setViews();
            engine.configurationTechnicalFile.setLangages(['en', 'e']);
            if (callback) callback(engine);
        });
    }

    private setViews(): void {
        const impactView = {
            alpha: -Math.PI / 2,
            beta: Math.PI / 3,
            radius: 3
        };
        const insideView = {
            alpha: Math.PI / 2,
            beta: Math.PI / 2,
            radius: 3
        };
        const thumbView = {
            alpha: -3.3,
            beta: Math.PI / 2,
            radius: 3
        };
        const wristView = {
            alpha: -Math.PI / 2,
            beta: Math.PI / 2,
            radius: 3
        };
        const innerThumbView = {
            alpha: Math.PI / 5,
            beta: (1.2 * Math.PI) / 2,
            radius: 3
        };
        const connectorThumbView = {
            alpha: -3.3,
            beta: Math.PI / 3.5,
            radius: 3
        };
        const gripView = {
            alpha: Math.PI / 2,
            beta: 2.4,
            radius: 3
        };
        const angles = [
            // FABRIC
            { ...{ name: 'Fabric_Impact_Zone' }, ...impactView },
            { ...{ name: 'Fabric_Wrist' }, ...wristView },
            { ...{ name: 'Fabric_Label_Colour' }, ...wristView },
            { ...{ name: 'Fabric_Thumb' }, ...thumbView },
            { ...{ name: 'Fabric_Inner_Thumb' }, ...innerThumbView },
            { ...{ name: 'Fabric_Thumb_Connector' }, ...connectorThumbView },
            { ...{ name: 'Fabric_Venum_Sticker' }, ...insideView },
            { ...{ name: 'Fabric_Velcro' }, ...wristView },
            { ...{ name: 'Fabric_Velcro_Strap' }, ...wristView },
            { ...{ name: 'Fabric_Grip_Bar' }, ...gripView },
            { ...{ name: 'Fabric_Right_Palm' }, ...insideView },
            { ...{ name: 'Fabric_Left_Palm' }, ...insideView },
            { ...{ name: 'Fabric_Pipings' }, ...wristView },
            { ...{ name: 'Fabric_Palm' }, ...insideView },
            { ...{ name: 'Fabric_Lace' }, ...insideView },
            // LOGO
            { ...{ name: 'Logo_Impact_Zone' }, ...impactView },
            { ...{ name: 'Logo_Small_Venum' }, ...insideView },
            { ...{ name: 'Logo_Venum_ImpactZone' }, ...impactView },
            { ...{ name: 'Logo_Impact_Zone_Venum' }, ...impactView },
            { ...{ name: 'Logo_Impact_Zone_Snake' }, ...impactView },
            { ...{ name: 'Logo_Sharp_Lines' }, ...wristView },
            { ...{ name: 'Logo_Label_Logos' }, ...wristView },
            { ...{ name: 'Logo_Main_Logo' }, ...impactView },
            { ...{ name: 'Logo_Main' }, ...impactView },
            { ...{ name: 'Logo_Velcro' }, ...wristView },
            { ...{ name: 'Logo_Custom' }, ...insideView },
            { ...{ name: 'Logo_Venum_Thumb' }, ...thumbView },
            { ...{ name: 'Logo_Venum_Thumb_Elite' }, ...thumbView },
            { ...{ name: 'Logo_Venum_Custom' }, ...insideView },
            { ...{ name: 'Logo_Leather' }, ...insideView },
            { ...{ name: 'Logo_Hand_Made' }, ...insideView },
            { ...{ name: 'Logo_Thumb' }, ...thumbView },
            // STITCHING
            { ...{ name: 'Stitching_Wrist' }, ...wristView },
            { ...{ name: 'Stitching_Pipings' }, ...insideView },
            { ...{ name: 'Stitching_Palm' }, ...insideView },
            // TEXT
            { ...{ name: 'Text_Thumb' }, ...thumbView },
            { ...{ name: 'Text_Left_Palm' }, ...insideView },
        ];
        const model = this.engine.contentsManager.getFirstModel();
        model.setMeshCameraAngles(angles);

        const defaultViews = [
            {
                alpha: -2.3,
                beta: 1.18,
                radius: 3
            },
            { ...insideView, ...{ radius: 2.7 } },
            thumbView,
            {
                alpha: 0.8,
                beta: 2.2,
                radius: 3
            },
            wristView,
        ];
        this.engine.toolScreenshot.setDefaultViews(defaultViews);
    }

    public addThumbTextInput(): void {
        const model = this.engine.contentsManager.getFirstModel();
        const variantMeshesList = model.getVariantMeshesMaterialsList();
        let maskVariantMeshes = find(variantMeshesList, (c) => c.meshes.includes('Logo_Custom'));
        if (!maskVariantMeshes) maskVariantMeshes = find(variantMeshesList, (c) => c.meshes.includes('Logo_Venum_Custom'));
        if (!maskVariantMeshes) return;

        // ThumbVariant
        const geometry = {
            type: 'text' as DecalType,
            name: 'Text_Thumb',
            meshes: ['Text_Thumb'],
            defaultText: '',
            position: new Vector3(
                -0.40715749763367814,
                0.6201476899558155,
                0.022920168262593854
            ),
            normal: new Vector3(
                -0.9837762428653981,
                -0.12002503961430959,
                -0.1333352685497156
            ),
            rotation: 63,
            size: 2,
            sourceMeshName: 'Logo_Thumb',
            sourceMeshStayVisible: false,
            maximumCharacter: 7,
            fonts: ['MrDafoe', 'LinoType', 'LastFeast'],
        };

        const thumbVariant: IVariantMeshesProperties = {
            nakerName: 'Text_Thumb',
            meshes: ['Text_Thumb'],
            decalGeometry: geometry,
            materials: maskVariantMeshes.materials
        };

        const added = model.buildPatternDecalWithSourceMesh(thumbVariant);
        if (added) {
            this.setDefaultDecalMaterial();
            model.variantMeshesPropertiesList.push(thumbVariant);
            this.engine.configurationVariantMesh.showVariantMeshesMaterialsList();
        }
    }

    public addPalmTextInput(): void {
        const model = this.engine.contentsManager.getFirstModel();
        const variantMeshesList = model.getVariantMeshesMaterialsList();
        let maskVariantMeshes = find(variantMeshesList, (c) => c.meshes.includes('Logo_Custom'));
        if (!maskVariantMeshes) maskVariantMeshes = find(variantMeshesList, (c) => c.meshes.includes('Logo_Venum_Custom'));
        if (!maskVariantMeshes) return;

        // PalmVariant
        const geometry = {
            type: 'text' as DecalType,
            name: 'Text_Left_Palm',
            meshes: ['Text_Left_Palm'],
            defaultText: '',
            position: new Vector3(
                0.19023902227115802,
                0.21883081172418628,
                -0.005320097240165911
            ),
            normal: new Vector3(
                0.4313128176232362,
                -0.034396895325821776,
                0.9015465084763234
            ),
            rotation: 90,
            size: 1,
            sourceMeshName: 'Fabric_Left_Palm',
            sourceMeshStayVisible: true,
            maximumCharacter: 7,
            fontSize: 1.6,
            fonts: ['MrDafoe', 'LinoType', 'LastFeast'],
        };

        const palmVariant: IVariantMeshesProperties = {
            nakerName: 'Text_Left_Palm',
            meshes: ['Text_Left_Palm'],
            decalGeometry: geometry,
            materials: maskVariantMeshes.materials
        };

        const added = model.buildPatternDecalWithSourceMesh(palmVariant);
        if (added) {
            this.setDefaultDecalMaterial();
            model.variantMeshesPropertiesList.push(palmVariant);
            this.engine.configurationVariantMesh.showVariantMeshesMaterialsList();
        }
    }

    private setDefaultDecalMaterial() {
        const model = this.engine.contentsManager.getFirstModel();
        // Manage default text color based on Logo Custom
        let variantMesh = model.getVariantMeshByName('Logo_Custom');
        if (!variantMesh) variantMesh = model.getVariantMeshByName('Logo_Venum_Custom');
        if (variantMesh) {
            if (model.patternDecals) {
                if (model.patternDecals[0]) model.patternDecals[0].setMaterial(variantMesh.material);
                if (model.patternDecals[1]) model.patternDecals[1].setMaterial(variantMesh.material);
            } else if (model.patternTexts) {
                if (model.patternTexts[0]) model.patternTexts[0].setMaterial(variantMesh.material);
                if (model.patternTexts[1]) model.patternTexts[1].setMaterial(variantMesh.material);
            }
            model.setVariantMeshes({ meshes: ['Text_Left_Palm'], material: variantMesh.material }, false);
            model.setVariantMeshes({ meshes: ['Text_Thumb'], material: variantMesh.material }, false);
        }
        this.engine.configurationVariantMesh.showVariantMeshesMaterialsList();
    }

    setEngineVersion(engineVersion: string) {
        this.engineVersion = '1.5.8';
    }

    // MASK TEST
    // addPalmTextInput() {
    //     let model = this.engine.contentsManager.getFirstModel()
    //     let variantMeshesList = model.getVariantMeshesMaterialsList()
    //     let maskVariantMeshes = find(variantMeshesList, (c) => { return c.meshes.includes("Logo_Custom") })
    //     if (!maskVariantMeshes) maskVariantMeshes = find(variantMeshesList, (c) => { return c.meshes.includes("Logo_Venum_Custom") })
    //     if (!maskVariantMeshes) return

    //     // PalmVariant
    //     let palmVariant: IVariantMeshesProperties = {
    //         nakerName: 'Text_Left_Palm',
    //         meshes: ['Text_Left_Palm'],
    //         decalGeometry: {
    //             type: 'mask',
    //             name: 'Text_Left_Palm',
    //             position: new Vector3(
    //                 0.19023902227115802,
    //                 0.21883081172418628,
    //                 -0.005320097240165911
    //             ),
    //             normal: new Vector3(
    //                 0.4313128176232362,
    //                 -0.034396895325821776,
    //                 0.9015465084763234
    //             ),
    //             rotation: 1,
    //             size: 0.1,
    //             sourceMeshName: 'Fabric_Left_Palm',
    //             sourceMeshStayVisible: true,
    //             maskList: [
    //                 'https://asset.naker.io/image/thumbnail/Airpods.png',
    //                 'https://asset.naker.io/image/banks/poly.png',
    //                 'https://d2uret4ukwmuoe.cloudfront.net/environment_v3/9.jpg'
    //             ],
    //         },
    //         materials: maskVariantMeshes.materials
    //     }
    //     model.addDecalVariant(palmVariant)
    //     this.engine.configurationVariantMesh.showVariantMeshesMaterialsList()
    // }
}

window.NakerStudioViewer = VenumViewer;
window.VenumViewer = VenumViewer;
